import { useCallback, useState } from 'react';

/**
 * Merge-style state hook, mirroring class-component `this.setState`.
 * Accepts a partial patch object or an updater `(prevState) => patch`.
 * Replaces the equivalent hook from the unmaintained `react-use` package.
 */
type SetStatePatch<T> = Partial<T> | ((prevState: T) => Partial<T>);

export default function useSetState<T extends object = Record<string, unknown>>(
  initialState: T = {} as T,
): [T, (patch: SetStatePatch<T>) => void] {
  const [state, set] = useState<T>(initialState);

  const setState = useCallback((patch: SetStatePatch<T>) => {
    set((prevState) => ({
      ...prevState,
      ...(patch instanceof Function ? patch(prevState) : patch),
    }));
  }, []);

  return [state, setState];
}
